import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const formatNum = (n) => {
  if (!n && n !== 0) return '—';
  return Math.round(n)?.toLocaleString('fr-FR')?.replace(/,/g, ' ');
};

const formatUSD = (n) => {
  if (!n && n !== 0) return '$0.00';
  return '$' + (parseFloat(n) || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ManagerComparison = ({ tableData, loading, serversData }) => {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState('diamonds');
  const [sortDir, setSortDir] = useState('desc');
  const [showAll, setShowAll] = useState(false);

  const serverColorMap = useMemo(() => {
    const map = {};
    (serversData || [])?.forEach(s => { map[s.id] = s?.color || '#888888'; });
    return map;
  }, [serversData]);

  const managerList = useMemo(() => {
    const managerStats = {};
    tableData?.forEach(row => {
      const managerId = row?.manager_id;
      const managerName = row?.users?.name || t('admin.performance.unassigned', 'Non assigné');
      const key = managerId || 'unassigned';

      if (!managerStats?.[key]) {
        managerStats[key] = {
          manager_id: managerId,
          manager_name: managerName,
          diamonds: 0,
          total_bonus: 0,
          charge: 0,
          net_agency: 0,
          active_creators: 0,
          servers: new Set()
        };
      }

      managerStats[key].diamonds += row?.diamonds || 0;
      managerStats[key].total_bonus += row?.total_bonus || 0;
      managerStats[key].charge += row?.charge || 0;
      managerStats[key].net_agency += row?.net_agency || 0;
      if (row?.diamonds > 0) managerStats[key].active_creators++;
      if (row?.server) managerStats?.[key]?.servers?.add(row?.server);
    });

    return Object.values(managerStats)?.map(m => ({ ...m, servers: [...m?.servers] }))?.sort((a, b) => b?.diamonds - a?.diamonds);
  }, [tableData, t]);

  const sortedList = useMemo(() => {
    if (!sortKey) return managerList;
    return [...managerList]?.sort((a, b) => {
      const aVal = a?.[sortKey] ?? 0;
      const bVal = b?.[sortKey] ?? 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [managerList, sortKey, sortDir]);

  const displayList = showAll ? sortedList : sortedList?.slice(0, 10);

  const handleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === 'desc') setSortDir('asc');
      else if (sortDir === 'asc') { setSortKey(''); setSortDir('desc'); }
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ color: 'rgba(255,255,255,0.2)', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: '#f59e0b', marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>;
  };

  const thStyle = (col) => ({
    padding: '10px 12px',
    fontSize: '11px',
    fontFamily: 'Sora, sans-serif',
    color: sortKey === col ? '#f59e0b' : 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    whiteSpace: 'nowrap',
    cursor: ['diamonds', 'total_bonus', 'net_agency', 'active_creators']?.includes(col) ? 'pointer' : 'default',
    userSelect: 'none',
    borderBottom: '1px solid #1f1f1f',
    textAlign: col === 'rank' ? 'center' : 'left',
  });

  if (loading) {
    return (
      <div className="rounded-xl p-6 animate-pulse" style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}>
        <div className="h-4 w-48 rounded mb-4" style={{ backgroundColor: '#1f1f1f' }} />
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="h-10 w-full rounded mb-2" style={{ backgroundColor: '#1a1a1a' }} />
        ))}
      </div>
    );
  }

  if (!managerList?.length) return null;

  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#0d0d0d' }}>
              <th style={thStyle('rank')}>{t('admin.performance.rank', 'Rang')}</th>
              <th style={thStyle('manager_name')}>{t('admin.performance.manager', 'Manager')}</th>
              <th style={thStyle('servers')}>{t('admin.performance.servers', 'Serveurs')}</th>
              <th
                style={thStyle('active_creators')}
                onClick={() => handleSort('active_creators')}
              >
                {t('admin.performance.activeCreators', 'Créateurs actifs')} <SortIcon col="active_creators" />
              </th>
              <th
                style={thStyle('diamonds')}
                onClick={() => handleSort('diamonds')}
              >
                {t('admin.performance.totalDiamonds', 'Total Diamonds')} <SortIcon col="diamonds" />
              </th>
              <th
                style={thStyle('total_bonus')}
                onClick={() => handleSort('total_bonus')}
              >
                {t('admin.performance.totalBonus', 'Total Bonus')} <SortIcon col="total_bonus" />
              </th>
              <th style={thStyle('charge')}>{t('admin.performance.charge', 'Charge')}</th>
              <th
                style={thStyle('net_agency')}
                onClick={() => handleSort('net_agency')}
              >
                {t('admin.performance.netAgency', 'Net Agence')} <SortIcon col="net_agency" />
              </th>
            </tr>
          </thead>
          <tbody>
            {displayList?.map((manager, idx) => {
              const originalRank = managerList?.findIndex(m => (m?.manager_id || 'unassigned') === (manager?.manager_id || 'unassigned')) + 1;
              const isFirst = originalRank === 1;
              return (
                <tr
                  key={manager?.manager_id || 'unassigned'}
                  style={{
                    borderBottom: '1px solid #1a1a1a',
                    borderLeft: isFirst ? '2px solid rgba(251,191,36,0.5)' : '2px solid transparent',
                    backgroundColor: isFirst ? 'rgba(251,191,36,0.03)' : 'transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = isFirst ? 'rgba(251,191,36,0.06)' : 'rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = isFirst ? 'rgba(251,191,36,0.03)' : 'transparent'; }}
                >
                  {/* Rang */}
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <span
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                        fontSize: '13px',
                        color: isFirst ? '#f59e0b' : 'rgba(255,255,255,0.4)',
                      }}
                    >
                      #{originalRank}
                    </span>
                  </td>
                  {/* Manager */}
                  <td style={{ padding: '10px 12px' }}>
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: isFirst ? '#fbbf24' : 'rgba(255,255,255,0.85)',
                      }}
                    >
                      {manager?.manager_name}
                    </span>
                  </td>
                  {/* Serveurs */}
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {manager?.servers?.length > 0 ? manager?.servers?.map(srv => {
                        const color = serverColorMap?.[srv] || '#888888';
                        return (
                          <span
                            key={srv}
                            style={{
                              padding: '2px 7px',
                              borderRadius: 4,
                              fontSize: '11px',
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                              backgroundColor: `${color}22`,
                              color,
                              border: `1px solid ${color}44`,
                            }}
                          >
                            {srv}
                          </span>
                        );
                      }) : (
                        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px' }}>—</span>
                      )}
                    </div>
                  </td>
                  {/* Créateurs actifs */}
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                      {manager?.active_creators}
                    </span>
                  </td>
                  {/* Total Diamonds */}
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontFamily: 'Sora, sans-serif', fontSize: '13px', fontWeight: 600, color: isFirst ? '#fbbf24' : 'rgba(255,255,255,0.85)' }}>
                      {formatNum(manager?.diamonds)}
                    </span>
                  </td>
                  {/* Total Bonus */}
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#22c55e' }}>
                      {formatUSD(manager?.total_bonus)}
                    </span>
                  </td>
                  {/* Charge */}
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                      {formatUSD(manager?.charge)}
                    </span>
                  </td>
                  {/* Net Agence */}
                  <td style={{ padding: '10px 12px' }}>
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: manager?.net_agency > 0 ? '#22c55e' : manager?.net_agency < 0 ? '#ef4444' : 'rgba(255,255,255,0.4)',
                      }}
                    >
                      {formatUSD(manager?.net_agency)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Voir tous / Réduire */}
      {managerList?.length > 10 && (
        <div
          style={{ padding: '12px 16px', borderTop: '1px solid #1a1a1a', textAlign: 'center' }}
        >
          <button
            onClick={() => setShowAll(v => !v)}
            style={{
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: '6px 20px',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '12px',
              fontFamily: 'Inter, sans-serif',
              cursor: 'pointer',
            }}
          >
            {showAll ? t('admin.performance.collapse', 'Réduire') : t('admin.performance.viewAll', 'Voir tous ({{count}})', { count: managerList?.length })}
          </button>
        </div>
      )}
    </div>
  );
};

export default ManagerComparison;
